/**
 * Component Auth Service
 *
 * Progressive authorization service for CR2 components.
 * Checks user's auth tier against component requirements.
 *
 * Auth Tier Hierarchy (0-7):
 *   0: Public Guest            → No auth required
 *   1: Authenticated User      → Valid token
 *   2: Organization Member     → User in ANY org
 *   3: Organization Role Req   → Specific org role (admin/super admin)
 *   4: Platform Org Member     → Member of platform org
 *   5: Department-Filtered     → Specific department
 *   6: DeptGroup-Filtered      → Specific dept group
 *   7: Executive/Admin Access  → Highest tier
 *
 * Created: 2026-04-21
 */

import { apiRequest } from '../utils/apiService';

import { SecureStorage } from '../utils/SecureStorage';
export interface ComponentAuthContext {
    sequence_num: number;
    visibility_auth_tier: number;
    activation_tier: number;
    required_role_id: number | null;
    required_org_hierarchy_id: number | null;
    visibility_department_id: number;
    visibility_deptgroup_id: number;
    perm_employee: boolean;
    perm_contractor: boolean;
    perm_advisor: boolean;
    perm_board: boolean;
}

export interface UserAuthContext {
    pfuser_uuid: string | null;
    access_token: string | null;
    is_authenticated: boolean;
    has_org_associations: boolean;
    is_organization_admin: boolean;
    is_organization_super_admin: boolean;
    is_tg_super_admin: boolean;
    user_departments: number[];
    user_dept_groups: number[];
    org_associations: Array<{
        organization_uuid: string;
        is_admin: boolean;
        is_super_admin: boolean;
        association_type_id: number;
    }>;
}

/**
 * Get user's current auth context from localStorage and session
 */
export const getUserAuthContext = (): UserAuthContext => {
    const pfuserUuid = SecureStorage.getPfuserUuid();
    const accessToken = SecureStorage.getAccessToken();

    // Parse stored user session (if available)
    const userSessionStr = SecureStorage.getUserSession();
    const userSession = userSessionStr ? JSON.parse(userSessionStr) : null;

    return {
        pfuser_uuid: pfuserUuid,
        access_token: accessToken,
        is_authenticated: !!(pfuserUuid && accessToken),
        has_org_associations: userSession?.has_org_associations || false,
        is_organization_admin: userSession?.is_organization_admin || false,
        is_organization_super_admin: userSession?.is_organization_super_admin || false,
        is_tg_super_admin: userSession?.is_tg_super_admin || false,
        user_departments: userSession?.user_departments || [],
        user_dept_groups: userSession?.user_dept_groups || [],
        org_associations: userSession?.org_associations || [],
    };
};

/**
 * Check if user meets component's auth tier requirements
 */
export const checkComponentAccess = (
    component: ComponentAuthContext,
    userContext: UserAuthContext,
): { allowed: boolean; reason?: string } => {
    const tier = component.visibility_auth_tier;

    // Tier 0: Public - always allowed
    if (tier === 0) {
        return { allowed: true };
    }

    // Tier 1+: Requires authentication
    if (tier >= 1 && !userContext.is_authenticated) {
        return { allowed: false, reason: 'Authentication required' };
    }

    // Tier 2+: Requires org association
    if (tier >= 2 && !userContext.has_org_associations) {
        return { allowed: false, reason: 'Organization membership required' };
    }

    // Tier 3: Requires specific org role (admin)
    if (tier === 3) {
        if (component.required_role_id === null) {
            // NULL = any admin role
            if (!userContext.is_organization_admin && !userContext.is_organization_super_admin) {
                return { allowed: false, reason: 'Organization admin role required' };
            }
        } else {
            // Specific role ID check (implement role lookup if needed)
            return { allowed: false, reason: 'Specific organization role required' };
        }
    }

    // Tier 4: Platform org member
    if (tier === 4) {
        // Check if user is member of platform organization (TTP_ID=1)
        const isPlatformMember = userContext.org_associations.some(
            (assoc) => assoc.organization_uuid === 'PLATFORM_ORG_UUID', // Replace with actual platform org UUID
        );
        if (!isPlatformMember) {
            return { allowed: false, reason: 'Platform organization membership required' };
        }
    }

    // Tier 5: Department-filtered
    if (tier === 5) {
        if (component.visibility_department_id !== 0) {
            const hasAccess = userContext.user_departments.includes(
                component.visibility_department_id,
            );
            if (!hasAccess) {
                return { allowed: false, reason: 'Department access required' };
            }
        }
    }

    // Tier 6: DeptGroup-filtered
    if (tier === 6) {
        if (component.visibility_deptgroup_id !== 0) {
            const hasAccess = userContext.user_dept_groups.includes(
                component.visibility_deptgroup_id,
            );
            if (!hasAccess) {
                return { allowed: false, reason: 'Department group access required' };
            }
        }
    }

    // Tier 7: Executive/Admin only
    if (tier === 7) {
        if (!userContext.is_tg_super_admin && !userContext.is_organization_super_admin) {
            return { allowed: false, reason: 'Executive or admin access required' };
        }
    }

    // Check ECAB permissions if set
    if (
        component.perm_employee ||
        component.perm_contractor ||
        component.perm_advisor ||
        component.perm_board
    ) {
        // Get user's association type from session
        const userAssocType = userContext.org_associations[0]?.association_type_id;

        if (component.perm_employee && userAssocType !== 3) {
            return { allowed: false, reason: 'Employee access required' };
        }
        if (component.perm_contractor && userAssocType !== 4) {
            return { allowed: false, reason: 'Contractor access required' };
        }
        if (component.perm_advisor && userAssocType !== 5) {
            return { allowed: false, reason: 'Advisor access required' };
        }
        if (component.perm_board && userAssocType !== 6) {
            return { allowed: false, reason: 'Board member access required' };
        }
    }

    return { allowed: true };
};

/**
 * Fetch component auth metadata from CR2
 */
export const fetchComponentAuthMetadata = async (
    sequenceNum: number,
): Promise<ComponentAuthContext | null> => {
    try {
        const result = await apiRequest('/api/cr2-registry', 'POST', {
            manage: 10, // GET_BY_ID
            Sequence_Num: sequenceNum,
            TTP_ID: 0,
        });

        if (result.data && result.data.length > 0) {
            const component = result.data[0];
            return {
                sequence_num: component.sequence_num,
                visibility_auth_tier: component.visibility_auth_tier || 1,
                activation_tier: component.activation_tier || 2,
                required_role_id: component.required_role_id,
                required_org_hierarchy_id: component.required_org_hierarchy_id,
                visibility_department_id: component.visibility_department_id || 0,
                visibility_deptgroup_id: component.visibility_deptgroup_id || 0,
                perm_employee: component.perm_employee || false,
                perm_contractor: component.perm_contractor || false,
                perm_advisor: component.perm_advisor || false,
                perm_board: component.perm_board || false,
            };
        }

        return null;
    } catch (err) {
        console.error(`Failed to fetch auth metadata for component ${sequenceNum}:`, err);
        return null;
    }
};

/**
 * Filter an array of components based on user's auth context
 */
export const filterComponentsByAuth = <T extends { seq: number }>(
    components: T[],
    componentAuthMap: Map<number, ComponentAuthContext>,
    userContext: UserAuthContext,
): T[] => {
    return components.filter((component) => {
        const authMeta = componentAuthMap.get(component.seq);
        if (!authMeta) {
            // No auth metadata = assume Tier 1 (authenticated)
            return userContext.is_authenticated;
        }

        const access = checkComponentAccess(authMeta, userContext);
        return access.allowed;
    });
};
